import type React from "react"
import Link from "next/link"

import { cn } from "@coval/ui/cn"
import { Plus } from "lucide-react"

import {
  SidebarNavShortcutKeys,
  toAriaKeyShortcuts,
} from "./SidebarNavShortcutKeys"

interface SidebarNavItemRowProps {
  href: string
  createHref?: string
  /** Only rendered alongside `createHref`. */
  createLabel?: string
  dataTestId?: string
  onMouseEnter?: () => void
  /** Intercepts the row link; may `preventDefault` to suppress navigation. */
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void
  active: boolean
  /** Marks the row as the current page for assistive tech. */
  isCurrentPage?: boolean
  /**
   * The global chord that reaches this row's destination (`"g t"`). Given one,
   * the row shows the keys at its trailing edge on hover; given none, it has
   * nothing to teach and stays bare.
   */
  shortcutKey?: string
  children: React.ReactNode
}

// The ring the nav list's `coval-focus-inset` gives a row that is itself the
// link, drawn on a row while its first link has focus. That link opts out of
// its own ring with `coval-custom-focus-ring coval-ancestor-focus-ring`.
export const DESTINATION_FOCUS_RING_CLASS =
  "has-[>a:first-child:focus-visible]:outline has-[>a:first-child:focus-visible]:outline-1 has-[>a:first-child:focus-visible]:-outline-offset-2 has-[>a:first-child:focus-visible]:outline-coval-focus-ring has-[>a:first-child:focus-visible]:transition-none"

export const SidebarNavItemRow = ({
  href,
  createHref,
  createLabel,
  dataTestId,
  onMouseEnter,
  onClick,
  active,
  isCurrentPage,
  shortcutKey,
  children,
}: SidebarNavItemRowProps) => {
  const rowClasses = cn(
    "group flex items-center gap-2 rounded-sm px-2 py-1 transition-colors",
    active ? "bg-surface-1" : "hover:bg-surface-1",
  )

  // The keys ride at the trailing edge of the link they belong to, inside it:
  // the strip they occupy is width the destination link already spans, and a
  // click there has to keep reaching that destination.
  const shortcutKeys = shortcutKey ? (
    <SidebarNavShortcutKeys shortcutKey={shortcutKey} className="ml-auto" />
  ) : null

  return !createHref ? (
    <Link
      href={href}
      data-testid={dataTestId}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
      aria-current={isCurrentPage ? "page" : undefined}
      aria-keyshortcuts={
        shortcutKey ? toAriaKeyShortcuts(shortcutKey) : undefined
      }
      className={rowClasses}
    >
      {children}
      {shortcutKeys}
    </Link>
  ) : (
    // The destination link fills the row but not its padding, so its own ring
    // would hug the label. The row draws the ring instead, on the same box a
    // row without a create link puts it on.
    <div
      className={cn(rowClasses, "pr-1", DESTINATION_FOCUS_RING_CLASS)}
      onMouseEnter={onMouseEnter}
    >
      <Link
        href={href}
        data-testid={dataTestId}
        onClick={onClick}
        aria-keyshortcuts={
          shortcutKey ? toAriaKeyShortcuts(shortcutKey) : undefined
        }
        className="coval-custom-focus-ring coval-ancestor-focus-ring flex min-w-0 flex-1 items-center gap-2 focus-visible:outline-none"
      >
        {children}
        {shortcutKeys}
      </Link>
      <Link
        href={createHref}
        aria-label={createLabel}
        className="flex h-5 w-5 shrink-0 items-center justify-center rounded-sm text-coval-text-muted opacity-0 transition-opacity hover:bg-surface-2 hover:text-coval-text group-hover:opacity-100 focus-visible:opacity-100"
      >
        <Plus className="h-3.5 w-3.5" />
      </Link>
    </div>
  )
}
